import { useEffect, useMemo, useState } from "react";
import type { LogoCard } from "../types";
import { LogoCardItem } from "./LogoCard";
import { useDebounce } from "use-debounce";

export const SearchCardList: React.FC<{
  items: LogoCard[];
  totalCount: number;
  isIndexPage?: boolean;
}> = ({ items, totalCount, isIndexPage = false }) => {
  const [search, setSearch] = useState<null | string>(null);
  const [debouncedSearch] = useDebounce(search, 150);
  const [allLogos, setAllLogos] = useState<LogoCard[]>([]);
  const [allLogosShown, setAllLogosShown] = useState(false);

  const filteredItems = useMemo(() => {
    if (!debouncedSearch || debouncedSearch.length < 2) {
      return allLogosShown ? allLogos : items;
    }

    const searchLower = debouncedSearch.toLowerCase();
    return (isIndexPage ? allLogos : items).filter(
      (item) =>
        item.id.includes(searchLower) ||
        item.countryId.toLowerCase().includes(searchLower)
    );
  }, [debouncedSearch, allLogosShown, allLogos]);

  useEffect(() => {
    if (!isIndexPage) {
      return;
    }

    fetch("/all-logos.json")
      .then((res) => res.json())
      .then((data) => setAllLogos(data));
  }, []);
  return (
    <>
      <div className="sticky top-[49px] md:top-[65px] z-50">
        <div className="relative w-full text-[16px]">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 text-neutral-500">
            <div className="pointer-events-none">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.3-4.3"></path>
              </svg>
            </div>
          </div>
          <input
            type="text"
            placeholder={`Search by team name or country...`}
            autoComplete="off"
            className="w-full border-neutral-200 border-l-0 border-t-0 bg-white p-3 px-11 placeholder-neutral-500 focus-visible:border-neutral-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-neutral-800 dark:bg-neutral-900 dark:focus-visible:border-neutral-800 dark:focus-visible:ring-offset-neutral-900"
            onChange={(e) => {
              setSearch(e.target.value);
            }}
          />
        </div>
      </div>
      <div className="flex flex-col gap-4 p-2 md:p-4">
        <h1 className="text-base text-neutral-500 dark:text-neutral-200">
          {isIndexPage ? "All Teams" : `${items[0].country} Teams`}
        </h1>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 px-2 md:px-4 mb-2 md:mb-4">
        {filteredItems.map((item) => (
          <LogoCardItem
            key={`${item.countryId}-${item.id}`}
            name={item.name}
            countryId={item.countryId}
            country={item.country}
            id={item.id}
            svg={item.svg}
            png={item.png}
          />
        ))}
      </div>
      {isIndexPage && !allLogosShown && !debouncedSearch ? (
        <div className="flex items-center justify-center mb-4">
          <button
            className="flex items-center space-x-2 relative h-10 rounded-full border border-neutral-200 dark:border-neutral-800 bg-transparent px-4 text-neutral-950 dark:text-white hover:bg-neutral-200/50 dark:hover:bg-neutral-800/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed active:translate-y-0.5 ring-offset-white dark:ring-offset-neutral-900"
            onClick={() => {
              setAllLogosShown(true);
            }}
          >
            <div className="flex items-center space-x-2 relative">
              <svg
                width="16"
                height="16"
                stroke="currentColor"
                strokeWidth="2"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 5v14"></path>
                <path d="m19 12-7 7-7-7"></path>
              </svg>{" "}
              <span>Load All logos</span>{" "}
              <span className="opacity-70">({totalCount - 20} more)</span>
            </div>
          </button>
        </div>
      ) : null}
    </>
  );
};
